import React from "react";
import { TextField, Switch, FormControlLabel, Box, Typography, Paper } from "@mui/material";

const SKIP_KEYS = ["id", "domains", "original_domain", "url_with_protocol",
  "admin_language", "current_theme", "created_at", "updated_at"];

const LABEL_OVERRIDES = {
  colors: "Colores",
  features: "Características",
  name: "Nombre",
  description: "Descripción",
  contact_email: "Email de contacto",
  whatsapp_phone_number: "WhatsApp",
  customer_accounts: "Tipo de cuenta",
  plan_name: "Plan",
  main_currency: "Moneda principal",
  main_language: "Idioma principal",
  business_id: "ID fiscal",
  business_name: "Razón social",
  business_address: "Dirección fiscal",
};

function friendlyLabel(key) {
  return LABEL_OVERRIDES[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

function isSimpleArray(val) {
  return Array.isArray(val) && val.every(v => typeof v !== "object" || v === null);
}

function isHexColor(str) {
  return typeof str === "string" && /^#[0-9a-fA-F]{3,6}$/.test(str);
}

const DynamicJsonForm = ({ data, onChange, path = "" }) => {
  if (data === null || data === undefined) return null;

  const handleChange = (key, value) => {
    const fullPath = path ? `${path}.${key}` : key;
    onChange(fullPath, value);
  };

  if (Array.isArray(data)) {
    if (isSimpleArray(data)) {
      return (
        <TextField
          label={friendlyLabel(path.split(".").pop())}
          value={data.join(", ")}
          onChange={(e) => onChange(path, e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
          fullWidth
          size="small"
          helperText="Valores separados por coma"
        />
      );
    }
    return (
      <Box sx={{ ml: 2 }}>
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>{friendlyLabel(path.split(".").pop())}</Typography>
        {data.map((item, idx) => (
          <DynamicJsonForm key={idx} data={item} onChange={onChange} path={`${path}.${idx}`} />
        ))}
      </Box>
    );
  }

  if (typeof data === "object" && data !== null) {
    const entries = Object.entries(data).filter(([k]) => !SKIP_KEYS.includes(k));
    if (entries.length === 0) return null;

    const label = friendlyLabel(path.split(".").pop());
    const isRoot = path === "";

    return (
      <Box sx={isRoot ? {} : { ml: 2, mt: 1, mb: 1 }}>
        {!isRoot && (
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "text.secondary" }}>
            {label}
          </Typography>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {entries.map(([key, value]) => {
            const fieldLabel = friendlyLabel(key);

            if (value !== null && typeof value === "object" && !Array.isArray(value)) {
              return (
                <Paper key={key} variant="outlined" sx={{ p: 1.5 }}>
                  <DynamicJsonForm
                    data={value}
                    onChange={onChange}
                    path={path ? `${path}.${key}` : key}
                  />
                </Paper>
              );
            }

            if (Array.isArray(value)) {
              return (
                <Box key={key}>
                  <DynamicJsonForm
                    data={value}
                    onChange={onChange}
                    path={path ? `${path}.${key}` : key}
                  />
                </Box>
              );
            }

            if (typeof value === "boolean") {
              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      checked={value}
                      onChange={(e) => handleChange(key, e.target.checked)}
                      size="small"
                    />
                  }
                  label={fieldLabel}
                />
              );
            }

            if (typeof value === "number") {
              return (
                <TextField
                  key={key}
                  label={fieldLabel}
                  value={value}
                  onChange={(e) => handleChange(key, parseFloat(e.target.value) || 0)}
                  fullWidth
                  size="small"
                  type="number"
                />
              );
            }

            if (isHexColor(value)) {
              const colorId = `color-${path}-${key}`;
              return (
                <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: value,
                      flexShrink: 0,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <input
                      id={colorId}
                      type="color"
                      value={value}
                      onChange={(e) => handleChange(key, e.target.value)}
                      style={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0,
                        cursor: "pointer",
                        width: "100%",
                        height: "100%",
                        padding: 0,
                        border: "none",
                      }}
                    />
                  </Box>
                  <TextField
                    value={value}
                    onChange={(e) => handleChange(key, e.target.value)}
                    fullWidth
                    size="small"
                    label={fieldLabel}
                  />
                </Box>
              );
            }

            return (
              <TextField
                key={key}
                label={fieldLabel}
                value={value ?? ""}
                onChange={(e) => handleChange(key, e.target.value)}
                fullWidth
                size="small"
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  return null;
};

export default DynamicJsonForm;
